import { useCallback, useMemo } from 'react';
import { Colors } from '@app/theme';
import {
  CSMRecommendation,
  CSMRecommendationType,
  useReadCSMRecommendation,
} from '@app/api';
import { groupBy } from 'lodash';
import { SessionNotification } from './SessionNotification';
import { useTranslation } from 'react-i18next';

interface Props {
  recommendations: CSMRecommendation[];
  sessionId: string;
  allowHide?: boolean;
}

interface IconByRecommendationType {
  [key: string]: {
    icon: string;
    color: Colors;
  };
}

export const iconByRecommendationType: IconByRecommendationType = {
  [CSMRecommendationType.ERROR]: {
    icon: 'ri-error-warning-line',
    color: 'red',
  },
  [CSMRecommendationType.WARN]: {
    icon: 'ri-error-warning-line',
    color: 'yellow',
  },
  [CSMRecommendationType.INFO]: {
    icon: 'ri-information-line',
    color: 'blue',
  },
};

const recommendationsTypeList = [
  CSMRecommendationType.ERROR,
  CSMRecommendationType.WARN,
  CSMRecommendationType.INFO,
];

export const RecommendationClient = ({
  recommendations,
  sessionId,
  allowHide = true,
}: Props) => {
  const { t } = useTranslation();
  const recommendationsByType = useMemo(() => {
    return groupBy(recommendations, 'type');
  }, [recommendations]);
  const { mutate: readRecommendation } = useReadCSMRecommendation(sessionId);

  const readRecommendationById = useCallback(
    (id: string) => {
      readRecommendation({ id });
    },
    [readRecommendation],
  );

  return (
    <>
      {recommendationsTypeList?.map((type) => {
        const recommendations = recommendationsByType[type];
        return recommendations?.map((recommendation) => (
          <SessionNotification
            key={recommendation.id}
            message={recommendation.message}
            color={iconByRecommendationType[type].color}
            icon={iconByRecommendationType[type].icon}
            buttonColor="blue"
            onClick={() => readRecommendationById(recommendation.id)}
            buttonText={
              allowHide ? t('sessions.details.monitoringAdmin.hide') : undefined
            }
          />
        ));
      })}
    </>
  );
};
